<script setup lang="ts">
import type { YearSource } from '@open-songster/shared'
import { computed } from 'vue'

/**
 * The origin of a track's year, shown as the provider's mark rather than a text
 * badge — the titles are what a curator scans, and two words of uppercase
 * competed with them.
 *
 * The marks are files in each app's `public/brand/` so the official assets can
 * be dropped in without touching code. Spotify's guidelines require their unmodified logo
 * when attributing their content.
 */
interface Props {
  source: YearSource | null
}

const props = defineProps<Props>()

// Relative to the app's base, so a deployment under a sub-path still finds
// the copies in its own `public/brand/`.
const BASE = import.meta.env.BASE_URL
const MARKS: Partial<Record<NonNullable<YearSource>, string>> = {
  spotify: `${BASE}brand/spotify.svg`,
  'spotify-fallback': `${BASE}brand/spotify.svg`,
  musicbrainz: `${BASE}brand/musicbrainz.svg`,
}

/** Every state still needs an accessible name; the mark alone is not one. */
const LABELS: Record<string, string> = {
  spotify: 'playlist.sourceSpotify',
  'spotify-fallback': 'playlist.sourceSpotifyFallback',
  musicbrainz: 'playlist.sourceMusicbrainz',
  manual: 'playlist.sourceManual',
  none: 'playlist.sourceNone',
}

const labelKey = computed(() => LABELS[props.source ?? 'none'] ?? 'playlist.sourceNone')
const markSrc = computed(() => (props.source ? MARKS[props.source] : undefined))
</script>

<template>
  <span
    class="source-mark"
    :class="{ 'source-mark--unverified': props.source === 'spotify-fallback' }"
    role="img"
    :aria-label="$t(labelKey)"
    :title="$t(labelKey)"
  >
    <img
      v-if="markSrc"
      class="source-mark__logo"
      :src="markSrc"
      alt=""
    />
    <!-- A person is not a provider, so this mark is ours rather than a logo. -->
    <span v-else-if="props.source === 'manual'" class="source-mark__manual" aria-hidden="true">
      ✎
    </span>
    <!-- Unresolved: nothing dated this track, so there is no provider to show. -->
    <span v-else class="source-mark__none" aria-hidden="true">—</span>
  </span>
</template>

<style scoped>
.source-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex: 0 0 auto;
}

.source-mark__logo {
  /* The official marks are not square (MusicBrainz is 127x144, Spotify
     236x225), so they are fitted inside the box rather than stretched to it.
     Forcing both axes would distort them, which brand guidelines forbid and
     which looks wrong besides. */
  width: auto;
  height: auto;
  max-width: 22px;
  max-height: 22px;
  object-fit: contain;
  /* Guidelines also forbid recolouring, so the mark keeps its own colour and
     is only dimmed. */
  opacity: 0.92;
}

.source-mark__manual {
  color: var(--color-accent);
  font-size: var(--text-md);
  line-height: 1;
}

.source-mark__none {
  color: var(--color-text-faint);
  font-family: var(--font-mono);
}

/**
 * The unverified case is the one a curator must notice: MusicBrainz was asked
 * and had nothing, so Spotify's date stands unchecked. An amber ring marks it
 * without altering the logo itself.
 */
.source-mark--unverified {
  border-radius: var(--radius-full);
  box-shadow: 0 0 0 2px var(--color-warn);
}
</style>
